import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { User, Session } from '@supabase/supabase-js';

// Singleton to store auth state and prevent duplicate fetches
let cachedUser: User | null = null;
let cachedSession: Session | null = null;
let initializationStarted = false; // Tracks if initialization has begun (prevents duplicate calls)
let sessionFetched = false; // Tracks if the initial session has been fetched (auth is ready)
let authListeners: Set<() => void> = new Set();

function notifyListeners() {
  authListeners.forEach((listener) => listener());
}

// Initialize auth once
function initializeAuth() {
  if (initializationStarted) return;
  initializationStarted = true;

  supabase.auth.getSession().then(({ data: { session } }) => {
    cachedSession = session;
    cachedUser = session?.user ?? null;
    sessionFetched = true; // Mark session as fetched AFTER the async call resolves
    notifyListeners();
  });

  supabase.auth.onAuthStateChange((_event, session) => {
    cachedSession = session;
    cachedUser = session?.user ?? null;
    notifyListeners();
  });
}

export function useAuth() {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    initializeAuth();

    const listener = () => forceUpdate({});
    authListeners.add(listener);
    return () => {
      authListeners.delete(listener);
    };
  }, []);

  return {
    user: cachedUser,
    session: cachedSession,
    loading: !sessionFetched,
    isAuthenticated: !!cachedUser,
  };
}

